import { useTranslation } from 'react-i18next';
import { router } from 'expo-router';
import { useCallback, useMemo } from 'react';
import {
  PaywallFeature,
  isPaywallError,
  getPaywallFeatureFromString,
} from '@/models/Paywall';
import { useCommunity } from '@/hooks/useCommunity';

export function usePaywallError() {
  const { t } = useTranslation();
  const community = useCommunity();

  const getPaywallMessage = useCallback(
    (feature?: PaywallFeature): string => {
      if (!feature) {
        return t('paywall.default');
      }

      const featureKey = PaywallFeature[feature] as keyof typeof PaywallFeature;
      const translationKey = `paywall.${featureKey}`;
      return t(translationKey, {
        defaultValue: t('paywall.default'),
        premiumPlanName: community?.premiumPlan ?? 'Premium',
      });
    },
    [t, community]
  );

  const getResetTimeMessage = useCallback(
    (resetTime?: string): string | undefined => {
      if (!resetTime) return undefined;

      try {
        const resetDate = new Date(resetTime);
        const now = new Date();
        const diffMs = resetDate.getTime() - now.getTime();

        if (diffMs <= 0) return undefined;

        const diffMinutes = Math.ceil(diffMs / (1000 * 60));

        if (diffMinutes < 60) {
          return t('paywall.resetIn.minutes', { count: diffMinutes });
        } else {
          const diffHours = Math.ceil(diffMinutes / 60);
          return t('paywall.resetIn.hours', { count: diffHours });
        }
      } catch {
        return undefined;
      }
    },
    [t]
  );

  const checkForPaywallError = useCallback(
    (
      error: any
    ): {
      isPaywall: boolean;
      message?: string;
      resetTimeMessage?: string;
      feature?: PaywallFeature;
    } => {
      if (!isPaywallError(error)) {
        return { isPaywall: false };
      }

      const feature = getPaywallFeatureFromString(error.data.feature);
      const message = getPaywallMessage(feature);
      const resetTimeMessage = getResetTimeMessage(error.data.resetTime);

      return {
        isPaywall: true,
        message,
        resetTimeMessage,
        feature,
      };
    },
    [getPaywallMessage, getResetTimeMessage]
  );

  const navigateToPlans = useCallback(() => {
    router.navigate('/account/plans');
  }, []);

  return useMemo(
    () => ({
      checkForPaywallError,
      getPaywallMessage,
      navigateToPlans,
    }),
    [checkForPaywallError, getPaywallMessage, navigateToPlans]
  );
}

